import { events } from "aws-amplify/data";
import { matchService } from "./matches/matchService_v2.js";
import { board } from "./game/gameBoard_v2.js";
import { dasModal } from "./modal_v2.js";
import { matchCard } from "./matches/matchCard_v2.js";
import { dateTimeUtils } from "./dateTimeUtils_v2.js";
import { TROUBLESHOOTING } from "./constants_v2.js";
import { identityService } from "./profile/identityService_v2.js";
import { eventHub } from "./eventHub_v2.js";
import { iPop } from "./ipop_v2.js";
import { utilsApp } from "./utils-app_v2.js";
import { colors } from "./colors_v2.js";
import { idem } from "./idem.mjs";
import { escapeHtml } from "./escapeHtml_v2.js";
import { endGamePop } from "./game/endGamePop_v2.js";
import { currentMatch } from "./matches/currentMatch_v2.js";
import { animateRotation, animateRemoval } from "./game/animations_v2.js";

var connections = [];
var createdMatchRecd = "";
var joinedMatchRecd = "";
var watchedMatchRecd = "";
var changedMatchRecd = "";

var subscriptionManagerState;

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

/**
 * MAIN ENTRY POINT
 */
export const subscribeMatchParticipants = async ({ userID, tokenString }) => {
  console.log(`[Subscription] Setting up subscription for userID: ${userID}`);
  const safeUserID = userID.replace("_", "");
  console.log(`[Subscription] Connecting to matchParticipants/${safeUserID}`);

  const connection = await events.connect("matchParticipants/" + safeUserID, {
    authMode: "lambda",
    authToken: tokenString,
  });
  
  console.log(`[Subscription] Connection established for ${safeUserID}`);

  const subscription = connection.subscribe({
    next: async (eventMessage) => {
      console.log(`[Subscription] Event received for ${userID}:`, eventMessage);
      const message = await eventMessage.event;
      console.log(`[Subscription] Parsed message:`, JSON.stringify(message, null, 2));

      if (TROUBLESHOOTING) {
        document.getElementById("troubleshootingInfo").innerText =
          `Event: matchParticipants/${userID} ${dateTimeUtils.compactDate()}`;
      }

      // Handle both match updates and chat messages
      // 1. Check if this is a chat message
      if (message && message.type === "chat") {
        console.log(`[Subscription] Processing chat message:`, message);
        // Import chat module dynamically to avoid circular imports
        const { chat } = await import("./chat/chat_v2.js");
        chat.handleIncomingMessage(message);
        return; // Don't process as match update
      }

      // A player or the owner removed us as a watcher. This event has no
      // match in it (we're no longer allowed to see it).
      if (message && message.action === "RemovedAsWatcher") {
        await handleRemovedAsWatcher(message);
        return;
      }

      // 2. Identity Updates (for match data)
      updateParticipantIdentities(message);

      // 3. Prepare Context (Snapshots differences before cache updates)
      const context = await prepareEventContext(message);
      if (!context) return;

      // 4. Logic Routing
      await routeSubscriptionMessage(context);
    },

    error: async (err) => {
      console.error(`Subscription Error for ${userID}:`, err);
      dasModal.renderNoX(`
          Timed Out
          <p>The session timed out due to inactivity.</p>
          <button id="inActiveRefresh">Reconnect</button><div id="reconnectSplainer" class="i">ⓘ</div>
      `);
      colors.setButtonColors();
    },
  });

  connections.push({ userID, connection, subscription });
  console.log(`[Subscription] Successfully subscribed ${userID} to matchParticipants/${safeUserID}`);
  console.log(`[Subscription] Total connections: ${connections.length}`);
};

/**
 * LOGIC ROUTER
 */
const routeSubscriptionMessage = async (context) => {
  const { matchReceived, isCurrentMatch, isEcho, isRewinding } = context;

  // Caching update for echoes for the current match
  if (isEcho && isCurrentMatch) {
    matchService.updateMatchInCache(matchReceived);
    if (currentMatch.getSize() === "normal") {
      currentMatch.render("normal");
      board.render();
    }
    
    // Update chat for echo updates
    try {
      const { chat } = await import("./chat/chat_v2.js");
      await chat.show(matchReceived);
    } catch (error) {
      console.warn("Chat system failed to update on echo:", error);
    }
    
    return;
  }

  if (matchReceived.state === "abandoned") {
    return handleAbandonedMatch(context);
  }

  if (isCurrentMatch) {
    if (isRewinding) {
      return handleRewindUpdate(context);
    }
    return handleActiveMatchUpdate(context);
  }

  // Background Match Notifications (Current bug fix area)
  return handleBackgroundMatchUpdate(context);
};

/**
 * PREPARATION HELPERS
 * This captures the "Delta" booleans while matchB4 is still the old version.
 */
const prepareEventContext = async (message) => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status !== 200) return null;
  const pAIdentity = pAIdentityResponse.identity;

  const matchReceived = message.match;
  const matchB4 = matchService
    .getCache()
    .find((m) => m.matchID === matchReceived.matchID);
  const currentMatchID = matchService.getCurrentMatchID().currentMatchID;

  const oldMoveCount = matchB4?.moves?.length ?? 0;
  const newMoveCount = matchReceived?.moves?.length ?? 0;
  // default pop ups to true
  // happens when they've never selected it OR its a temp identity
  const joinedPlayerPop = pAIdentity?.joinedPlayerPop ?? true;
  const joinedWatcherPop = pAIdentity?.joinedWatcherPop ?? true;
  const changedPop = pAIdentity?.changedPop ?? true;
  const createdPop = pAIdentity?.createdPop ?? true;

  return {
    message,
    matchReceived,
    matchB4,
    pAUserID: pAIdentity.userID,
    pAIdentity: pAIdentity,
    isCurrentMatch: currentMatchID === matchReceived.matchID,
    isEcho: idem.keyExists(message.idempotencyKey),
    isRewinding: board.getRewindOffset() !== 0,
    action: message?.action ?? "",
    isNewMove: newMoveCount > oldMoveCount,
    isStateChange: matchB4 && matchB4.state !== matchReceived.state,
    lastMoveObj:
      matchReceived?.moves?.length > 0 ? matchReceived.moves.at(-1) : null,
    joinedPlayerPop,
    joinedWatcherPop,
    changedPop,
    createdPop,
  };
};

const updateParticipantIdentities = (message) => {
  const p1Identity = message?.p1Identity ?? {};
  const p2Identity = message?.p2Identity ?? {};
  if (Object.keys(p1Identity).length > 0) {
    identityService.updateIdentity(message.p1Identity);
  }
  if (Object.keys(p2Identity).length > 0) {
    identityService.updateIdentity(message.p2Identity);
  }
};

/**
 * We were removed as a watcher of message.matchID, by message.removedByName.
 * If it's the match on screen: hide its chat right away, explain in a pop-up,
 * and when that's closed, move to this identity's most recent active match,
 * or show "There is no match in progress" if there isn't one.
 * If it's another match: explain, and just drop it from the cache.
 */
const handleRemovedAsWatcher = async (message) => {
  const matchID = message?.matchID;
  if (typeof matchID !== "string" || matchID.length === 0) return;
  const isCurrent =
    matchService.getCurrentMatchID().currentMatchID === matchID;
  const who =
    typeof message.removedByName === "string" && message.removedByName.length > 0
      ? escapeHtml(message.removedByName)
      : "A player";

  if (isCurrent) {
    // can't chat in it anymore (the server refuses too)
    const { chat } = await import("./chat/chat_v2.js");
    chat.hide();
  } else {
    matchService.removeMatchFromCache(matchID);
  }

  dasModal.close();
  dasModal.render(
    `<div class="max flexColumn">
      <p>Removed From Watching</p>
      <p>${who} removed you from watching ${isCurrent ? "this match" : "one of your matches"}.</p>
      <p>You won't see its moves or chat anymore.</p>
    </div>`,
    { onClose: isCurrent ? () => moveOffRemovedMatch(matchID) : undefined },
  );
};

const moveOffRemovedMatch = async (matchID) => {
  matchService.removeMatchFromCache(matchID);
  if (currentMatch.getSize() === "normal") {
    currentMatch.render("small");
  }
  // picks the most recent active match where we're a player, or none
  await matchService.loadPlayingAsMatches({ createMatch: false });
  const result = matchService.getCurrentMatch();
  const { chat } = await import("./chat/chat_v2.js");
  if (result.status === 200) {
    await chat.show(result.currentMatch);
    return;
  }
  chat.hide();
  const { profile } = await import("./profile/profile_v2.js");
  profile.renderNoMatchInProgressPop();
};

/**
 * SCENARIO HANDLERS
 */
const handleAbandonedMatch = (context) => {
  const { matchReceived, changedPop } = context;
  board.render();
  if (changedPop) {
    renderWhatChangedPop({
      matchReceived,
      whatChanged: "state",
      text: `This match changed to: ${matchService.getFriendlyState(matchReceived.state)}<div id="whatChangedStatePlainer" class="i">ⓘ</div>`,
    });
  }
};

const handleActiveMatchUpdate = async (context) => {
  const { matchReceived, action, isNewMove } = context;

  if (isNewMove && action !== "updateToFinished") {
    await animateRemoteMove(context);
    return;
  }

  matchService.updateMatchInCache(matchReceived);
  if (currentMatch.getSize() === "normal") {
    currentMatch.render("normal");
  }
  const isPlayer = await matchService.pAIsPlayer();

  if (matchReceived.state === "finished" && isPlayer) {
    endGamePop.render();
  } else if (!isPlayer) {
    handleWatcherSpecificUpdates(context);
  }

  board.render();
  
  // Update chat when current match changes
  try {
    const { chat } = await import("./chat/chat_v2.js");
    await chat.show(matchReceived);
  } catch (error) {
    console.warn("Chat system failed to update:", error);
  }
};

const handleWatcherSpecificUpdates = (context) => {
  const { changedPop, matchReceived, matchB4, pAIdentity } = context;
  if (!changedPop) return;

  if (matchB4?.state === "notifyResign" && matchReceived.state === "finished") {
    const who =
      matchReceived.turn === matchReceived.p1UserID
        ? matchReceived.p1Name
        : matchReceived.p2Name;
    renderWhatChangedPop({
      matchReceived,
      whatChanged: "state",
      text: `${escapeHtml(who)} has resigned.`,
    });
  }
  const winnerName = matchReceived?.winnerName ?? false;

  if (
    matchB4?.state === "started" &&
    matchReceived.state === "finished" &&
    winnerName
  ) {
    renderWhatChangedPop({
      matchReceived,
      whatChanged: "state",
      text: `${escapeHtml(winnerName)} has won.`,
    });
  }

  if (matchB4?.state === "drawOffered" && matchReceived.state === "draw") {
    const who =
      matchReceived.turn === matchReceived.p1UserID
        ? matchReceived.p1Name
        : matchReceived.p2Name;
    renderWhatChangedPop({
      matchReceived,
      whatChanged: "state",
      text: `${escapeHtml(who)} offered a draw that was accepted.`,
    });
  }
};

const handleRewindUpdate = (context) => {
  const { isNewMove, isStateChange, matchReceived } = context;

  if (isNewMove) {
    renderNewMoveInARewindPop();
  } else if (isStateChange) {
    renderStateChangeInARewindPop(matchReceived.state);
  }
};

const handleBackgroundMatchUpdate = async (context) => {
  const {
    message,
    matchReceived,
    pAUserID,
    action,
    changedPop,
    createdPop,
    joinedPlayerPop,
    joinedWatcherPop,
    isNewMove,
    isStateChange,
  } = context;
  const whoJoined = message?.joiner;

  if (action.includes("Created") || action.includes("Joined")) {
    if (whoJoined === pAUserID || matchReceived.ownerUserID === pAUserID) {
      //gotta be in the cache before it can be the current match
      await matchService.updateMatchInCache(matchReceived);
      await matchService.setCurrentMatchID(matchReceived.matchID);

      board.render();
      return;
    }
    if (action.includes("Created") && createdPop) {
      renderNotifyCreatedPop(matchReceived);
      return;
    }

    const isJoinerPlayer =
      whoJoined === matchReceived.p1UserID ||
      whoJoined === matchReceived.p2UserID;

    if (isJoinerPlayer && joinedPlayerPop) {
      renderPlayerJoinedPop({ matchReceived, whoJoined });
    } else if (!isJoinerPlayer && joinedWatcherPop) {
      renderWatcherJoinedPop({ matchReceived, whoJoined });
    }
    return;
  }

  // Handle move/state changes in matches the user is NOT currently looking at

  if (changedPop) {
    let whatChanged = null;
    let text = null;

    if (isNewMove) {
      whatChanged = "move";
      text = "There is a new move in your other match:";
    } else if (isStateChange) {
      whatChanged = "state";
      text = `This match changed to: ${matchService.getFriendlyState(matchReceived.state)}<div id="whatChangedStatePlainer" class="i">ⓘ</div>`;
    }

    if (whatChanged) renderWhatChangedPop({ matchReceived, whatChanged, text });
  }

  // Update the cache AFTER we've checked for differences
  matchService.updateMatchInCache(matchReceived);
};

/**
 * ANIMATION INTEGRATION
 */
const animateRemoteMove = async (context) => {
  const { matchReceived, matchB4, lastMoveObj } = context;
  if (!lastMoveObj) return;

  const coords = lastMoveObj.coords;
  // Match JSON player IDs (e.g. "6EPQNPH") to local "playerOne"/"playerTwo" strings
  const player =
    lastMoveObj.player === matchReceived.p1UserID ? "playerOne" : "playerTwo";

  board.setHighlightCoords(coords);
  board.setHighlightState(true);
  board.renderGrid();
  await delay(1000);
  board.setHighlightState(false);

  switch (lastMoveObj.action) {
    case "push":
      await board.takeAction({
        coords,
        player,
        animateOnly: true,
        matchReceived,
      });
      break;

    case "rotate":
      // Look up piece in the old match state (matchB4) to get start direction
      const currentPiece = matchB4.pieces.find(
        (p) =>
          p.coords.q === coords.q &&
          p.coords.r === coords.r &&
          p.coords.s === coords.s,
      );

      await animateRotation({
        coords,
        startDir: currentPiece?.direction,
        endDir: lastMoveObj.direction,
        player,
      });

      matchService.updateMatchInCache(matchReceived);
      board.render();
      break;

    case "remove":
      await animateRemoval({ coords, player });
      matchService.updateMatchInCache(matchReceived);
      board.render();
      break;

    default:
      matchService.updateMatchInCache(matchReceived);
      board.render();
      break;
  }
};

const handleInActiveRefresh = async () => {
  const inActiveRefresh = document.getElementById("inActiveRefresh");
  inActiveRefresh.disabled = true;
  inActiveRefresh.innerText = "Submitting...";
  document.body.style.cursor = "waiting";
  const pAIdentityResponse = await identityService.getPlayingAs();
  inActiveRefresh.disabled = false;
  inActiveRefresh.innerText = "Reconnect";
  document.body.style.cursor = "default";
  dasModal.close();
  if (pAIdentityResponse.status != 200) {
    dasModal.renderNoX(
      `Unable to reconnect<p>If you were logged in, you can probably continue the match after refreshing your browser and logging in again.</p><p>If you were playing without logging in, then the match can not be recovered.</p>`,
    );
    return;
  }
  // if we got this far, the refresh token worked. We have a new access token and refresh token.
  // so lets try and re-establish the subscription:
  const params = {
    userID: pAIdentityResponse.identity.userID,
    tokenString:
      "Bearer " +
      utilsApp.base64UrlEncode(pAIdentityResponse.identity.identityToken),
  };
  await closeAllSubscriptions();
  await subscribeMatchParticipants(params);
};

const renderReconnectSplainer = (event) => {
  iPop.render({
    text: `Reconnect should work for about 36 hours. 
    <p>After that, people with accounts can refresh their browser and log in again.</p>
    <p>If you were playing without logging in, then the match can not be recovered after that point.<p>`,
    event,
  });
};

const updateTroubleshooting = () => {
  let string = "";
  for (const connection of connections) {
    string = string + " " + connection.userID;
  }
  document.getElementById("troubleshootingInfo2").innerText =
    "Subscriptions: " + string;
};

const renderNotifyCreatedPop = (match) => {
  let matchItemsHTML = matchCard.generateHTML(match);
  dasModal.close();
  dasModal.render(`
    <p>New Match</p>
    ${escapeHtml(match.ownerName)} added you to their match:<div id="notifySplainer" class="i">ⓘ</div>
    <br><br>
    <div id="matchCardCreated">
    ${matchItemsHTML}
    </div>
    <br>
    Would you like to switch to it now?
    <div class="flexRowSub">
      <button class="button" id="createdYes">Yes</button>
      <button class="button" id="createdNo">No</button>
      <button class="button" id="createdDontShow">Don't Show This Again</button><div id="dontShowSplainer" class="i">ⓘ</div>
    </div>
    `);
  createdMatchRecd = match.matchID;
};

const handleCreatedDontShow = async () => {
  createdMatchRecd = "";
  document.body.style.cursor = "wait";
  document.getElementById("createdDontShow").disabled = true;
  await identityService.setCreatedPop(false);
  document.getElementById("createdDontShow").disabled = false;
  document.body.style.cursor = "default";
  dasModal.close();
};

const handleCreatedYes = async () => {
  await matchService.setCurrentMatchID(createdMatchRecd);
  board.render();
  createdMatchRecd = "";
  dasModal.close();
};

const handleCreatedNo = () => {
  createdMatchRecd = "";
  dasModal.close();
};

const renderNotifySplainer = (event) => {
  iPop.render({
    text: "You can also find it under Recent Matches",
    event,
  });
};

const renderPlayerJoinedPop = async ({ matchReceived, whoJoined }) => {
  const whoJoinedIdentity = (await identityService.getIdentity(whoJoined))
    .identity;
  let blurbDisplay = "";
  if (whoJoinedIdentity?.blurb) {
    blurbDisplay = ` '${escapeHtml(whoJoinedIdentity.blurb)}'`;
  }
  let matchItemsHTML = matchCard.generateHTML(matchReceived);
  dasModal.close();

  dasModal.render(`
    <p>Player Joined</p>
    ${escapeHtml(whoJoinedIdentity.name)}${blurbDisplay} has joined as a player in one of your other matches<div id="notifySplainer" class="i">ⓘ</div>
    <br><br>
    <div id="matchCardJoined">
    ${matchItemsHTML}
    </div>
    <br>
    Would you like to switch to it now?
    <div class="flexRowSub">
      <button class="button" id="joinedYes">Yes</button>
      <button class="button" id="joinedNo">No</button>
      <button class="button" id="joinedPlayerDontShow">Don't Show This Again</button><div id="dontShowSplainer" class="i">ⓘ</div>
    </div>
    `);
  joinedMatchRecd = matchReceived.matchID;
};

const handleJoinedPlayerDontShow = async () => {
  joinedMatchRecd = "";
  document.body.style.cursor = "wait";
  document.getElementById("joinedPlayerDontShow").disabled = true;
  await identityService.setJoinedPlayerPop(false);
  document.getElementById("joinedPlayerDontShow").disabled = false;
  document.body.style.cursor = "default";
  dasModal.close();
};

const handleJoinedYes = async () => {
  await matchService.setCurrentMatchID(joinedMatchRecd);
  board.render();
  joinedMatchRecd = "";
  dasModal.close();
};

const handleJoinedNo = () => {
  joinedMatchRecd = "";
  dasModal.close();
};

const renderWatcherJoinedPop = async ({ matchReceived, whoJoined }) => {
  const whoJoinedIdentity = (await identityService.getIdentity(whoJoined))
    .identity;
  let blurbDisplay = "";
  if (whoJoinedIdentity?.blurb) {
    blurbDisplay = ` '${escapeHtml(whoJoinedIdentity.blurb)}'`;
  }
  let matchItemsHTML = matchCard.generateHTML(matchReceived);
  dasModal.close();
  dasModal.render(`
    <p>Watcher Joined</p>
    ${escapeHtml(whoJoinedIdentity.name)}${blurbDisplay} has started watching one of your other matches<div id="notifySplainer" class="i">ⓘ</div>
    <br><br>
    <div>
    ${matchItemsHTML}
    </div id="matchCardJoinedWatcher">
    <br>
    Would you like to switch to it now?
    <div class="flexRowSub">
      <button class="button" id="joinedWatcherYes">Yes</button>
      <button class="button" id="joinedWatcherNo">No</button>
      <button class="button" id="joinedWatcherDontShow">Don't Show This Again</button><div id="dontShowSplainer" class="i">ⓘ</div>
    </div>
    `);
  watchedMatchRecd = matchReceived.matchID;
};

const handleJoinedWatcherDontShow = async () => {
  watchedMatchRecd = "";
  document.body.style.cursor = "wait";
  document.getElementById("joinedWatcherDontShow").disabled = true;
  await identityService.setJoinedWatcherPop(false);
  document.getElementById("joinedWatcherDontShow").disabled = false;
  document.body.style.cursor = "default";
  dasModal.close();
};

const renderDontShowSplainer = (event) => {
  iPop.render({
    text: "If you change your mind, you can turn these pop ups back on in settings (gear icon)",
    event,
  });
};

const handleJoinedWatcherYes = async () => {
  await matchService.setCurrentMatchID(watchedMatchRecd);
  board.render();
  watchedMatchRecd = "";
  dasModal.close();
};

const handleJoinedWatcherNo = () => {
  watchedMatchRecd = "";
  dasModal.close();
};

const renderWhatChangedStatePlainer = (event) => {
  const text = currentMatch.generateStateSplainerHTML(subscriptionManagerState);
  iPop.render({
    text,
    event,
  });
};

const renderNewMoveInARewindPop = () => {
  dasModal.close();

  dasModal.render(`
    <p>Opponent Move</p>
    Your opponent has made a new move.
    <br><br>
    Use the <span class="noWrap">« double arrow »</span> buttons to see it.
    <br>
    `);
};

const renderStateChangeInARewindPop = (state) => {
  subscriptionManagerState = state;
  dasModal.close();
  dasModal.render(`
    <p>State Change</p>
    The state of this match has <span class="noWrap">changed to: ${matchService.getFriendlyState(state)}<div id="whatChangedStatePlainer" class="i">ⓘ</div></span>
    <br><br>
    Use the <span class="noWrap">« double arrow »</span> buttons under the board to see it.
    <br>
    `);
};

const renderWhatChangedPop = ({ matchReceived, whatChanged, text }) => {
  const currentMatch = matchService.getCurrentMatch().currentMatch;
  const isCurrentMatch = currentMatch.matchID === matchReceived.matchID;

  let yesNoButtons = "";
  let question = "";
  if (!isCurrentMatch) {
    yesNoButtons = `<button class='button' id='changedYes'>Yes</button><button class='button' id='changedNo'>No</button>`;
    question = "Would you like to switch to it now?";
  }
  let matchItemsHTML = matchCard.generateHTML(matchReceived);
  let title = "";

  switch (whatChanged) {
    case "move":
      title = "New Move";
      break;
    case "state":
      title = "State Change";
      break;
    default:
      title = "Update";
  }
  dasModal.close();

  dasModal.render(`
    <p>${title}</p>
    ${text}
    <br><br>
    <div id="matchCardChanged">
    ${matchItemsHTML}
    </div>
    <br>
    ${question}
    <div class="flexRowSub">
      ${yesNoButtons}
      <button class="button" id="changedDontShow">Don't Show This Again</button><div id="dontShowSplainer" class="i">ⓘ</div>
    </div>
    `);
  subscriptionManagerState = matchReceived.state;
  changedMatchRecd = matchReceived.matchID;
};

const handleChangedDontShow = async () => {
  changedMatchRecd = "";
  document.body.style.cursor = "wait";
  document.getElementById("changedDontShow").disabled = true;
  await identityService.setChangedPop(false);
  document.getElementById("changedDontShow").disabled = false;
  document.body.style.cursor = "default";
  dasModal.close();
};

const handleChangedYes = async () => {
  await matchService.setCurrentMatchID(changedMatchRecd);
  board.render();
  changedMatchRecd = "";
  dasModal.close();
};

const handleChangedNo = () => {
  changedMatchRecd = "";
  dasModal.close();
};

const closeAllSubscriptions = async () => {
  for (const connection of connections) {
    connection.subscription.unsubscribe();
    connection.connection.close();
  }
  connections = [];
};
eventHub.registerEvent(
  "click",
  "whatChangedStatePlainer",
  renderWhatChangedStatePlainer,
);
eventHub.registerEvent("click", "dontShowSplainer", renderDontShowSplainer);
eventHub.registerEvent("click", "notifySplainer", renderNotifySplainer);

eventHub.registerEvent("click", "createdNo", handleCreatedNo);
eventHub.registerEvent("click", "createdYes", handleCreatedYes);
eventHub.registerEvent("click", "matchCardCreated", handleCreatedYes);
eventHub.registerEvent("click", "createdDontShow", handleCreatedDontShow);

eventHub.registerEvent("click", "joinedYes", handleJoinedYes);
eventHub.registerEvent("click", "matchCardJoined", handleJoinedYes);
eventHub.registerEvent("click", "joinedNo", handleJoinedNo);
eventHub.registerEvent(
  "click",
  "joinedPlayerDontShow",
  handleJoinedPlayerDontShow,
);

eventHub.registerEvent("click", "joinedWatcherYes", handleJoinedWatcherYes);
eventHub.registerEvent(
  "click",
  "matchCardJoinedWatcher",
  handleJoinedWatcherYes,
);
eventHub.registerEvent("click", "joinedWatcherNo", handleJoinedWatcherNo);
eventHub.registerEvent(
  "click",
  "joinedWatcherDontShow",
  handleJoinedWatcherDontShow,
);

eventHub.registerEvent("click", "changedNo", handleChangedNo);
eventHub.registerEvent("click", "changedYes", handleChangedYes);
eventHub.registerEvent("click", "matchCardChanged", handleChangedYes);
eventHub.registerEvent("click", "changedDontShow", handleChangedDontShow);

eventHub.registerEvent("click", "reconnectSplainer", renderReconnectSplainer);
eventHub.registerEvent("click", "inActiveRefresh", handleInActiveRefresh);

export const subscriptionManager = {
  subscribeMatchParticipants,
  closeAllSubscriptions,
};
